import { Box, FormControl, MenuItem, OutlinedInput, Select } from '@mui/material';

import type { ExtendedSeriesOptionsType } from '@/components/Chart/types/highcharts';
import { getSeriesId } from '@/components/Chart/utils/seriesVisibility';

import useLegendDropdown from './useLegendDropdown';

const LegendItem = ({ series }: { series: ExtendedSeriesOptionsType }) => {
  const unitPrice = series.custom ? `(${series.custom.Currency} / ${series.custom.Units})` : '';
  const seriesColor = (series.color ?? series.custom?.color ?? '#E84E0F') as string;
  return (
    <Box style={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
      <div
        style={{
          width: '8px',
          height: '2px',
          backgroundColor: seriesColor,
          display: 'inline-block',
          marginRight: 4
        }}
      />
      <Box
        style={{
          maxWidth: 550,
          width: '100%',
          textOverflow: 'ellipsis',
          overflow: 'hidden',
          whiteSpace: 'nowrap'
        }}
      >
        {series.name} {unitPrice}
      </Box>
    </Box>
  );
};

const LegendDropdown = ({
  series,
  hiddenSeriesIds,
  setHiddenSeriesIds
}: {
  series: ExtendedSeriesOptionsType[];
  hiddenSeriesIds: string[];
  setHiddenSeriesIds: (ids: string[]) => void;
}) => {
  const { selectedSeriesIds, allChartSeries, handleLegendItemClick, isSeriesDisabled } =
    useLegendDropdown({
      series,
      hiddenSeriesIds,
      setHiddenSeriesIds
    });

  return (
    <Box sx={{ minWidth: 120, pr: 5, pl: 1, pb: 2 }}>
      <FormControl fullWidth>
        <Select
          multiple
          id='legend-select'
          input={<OutlinedInput inputProps={{ pl: 0, height: 34 }} />}
          MenuProps={{
            anchorOrigin: {
              vertical: 'top',
              horizontal: 'left'
            },
            transformOrigin: {
              vertical: 'top',
              horizontal: 'left'
            },
            MenuListProps: {
              sx: {
                padding: 0
              }
            },
            PaperProps: {
              sx: {
                border: '1px solid #E84E0F',
                borderBottom: 0,
                borderLeft: 0
              }
            }
          }}
          renderValue={selected => {
            const selectedIds = typeof selected === 'string' ? [selected] : selected;
            const active = allChartSeries.find(item => selectedIds[0] === getSeriesId(item));
            return active ? <LegendItem series={active} /> : null;
          }}
          size='small'
          sx={{
            height: 34,
            '.MuiSelect-select': {
              paddingLeft: 1
            }
          }}
          value={selectedSeriesIds}
          onChange={handleLegendItemClick}
        >
          {allChartSeries?.map(series => {
            const seriesId = getSeriesId(series);
            if (!seriesId) return null;
            return (
              <MenuItem
                key={seriesId}
                disabled={isSeriesDisabled(series)}
                sx={{ '&.MuiButtonBase-root': { pl: 1 } }}
                value={seriesId}
              >
                <LegendItem series={series} />
              </MenuItem>
            );
          })}
        </Select>
      </FormControl>
    </Box>
  );
};

export default LegendDropdown;
